import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { calculateOrderTotals } from '@/lib/utils';
import { formatCurrency, useCreateOrder } from '@/services/order-service';
import { useProductVariants } from '@/services/product-variant-service';
import { Customer } from '@/types/customer-type';
import { FulfillmentCarrier, FulfillmentMethod } from '@/types/fulfillment-type';
import { OrderCreateFormValues, orderCreateSchema, OrderStatus } from '@/types/order-type';
import { zodResolver } from '@hookform/resolvers/zod';
import { Calendar, MapPin, Phone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../ui/button';
import { Form, FormField, FormItem, FormMessage } from '../ui/form';
import { OrderItemsEditor } from './OrderItemsEditor';
interface CreateOrderDialogProps {
  currentCustomer?: Customer;
  isOpenning: boolean;
  onCloseDialog?: () => void;
  initialData?: Partial<OrderCreateFormValues>;
  onSubmitOrder?: (success: boolean, orderId?: string | null) => void;
}

export default function CreateOrderDialog({
  currentCustomer,
  isOpenning,
  onCloseDialog,
  initialData,
  onSubmitOrder,
}: CreateOrderDialogProps) {
  const createOrderMutation = useCreateOrder();
  const [isOpenOrderDialog, setIsOpenOrderDialog] = useState<boolean>(false);
  const { data: variantsData } = useProductVariants();
  const allVariants = variantsData || [];
  useEffect(() => {
    setIsOpenOrderDialog(isOpenning);
  }, [isOpenning]);
  const form = useForm<OrderCreateFormValues>({
    resolver: zodResolver(orderCreateSchema),
    mode: 'onChange',
    defaultValues: {
      customerId: currentCustomer?.id,
      status: OrderStatus.DRAFT,
      notes: '',
      items: [],
      fulfillment: {
        method: FulfillmentMethod.DELIVERY_CITY,
        carrier: FulfillmentCarrier.VIREAK_BUNTHAM,
        price: '2',
      },
      ...initialData,
    },
  });
  const watchFulfillmentPrice = form.watch('fulfillment.price');
  const watchItems = form.watch('items');

  const totals = useMemo(() => {
    const fulfillmentPrice = parseFloat(watchFulfillmentPrice || '0');
    const items = watchItems || [];

    const itemsForCalc = items.map((i) => {
      const v = allVariants.find((v) => String(v.id) === String(i.variantId));
      const unitPrice = v ? Number(v.price || 0) : 0;
      return { unitPrice, quantity: i.quantity };
    });

    return calculateOrderTotals({
      items: itemsForCalc,
      discountPercentage: 0,
      deliveryFee: fulfillmentPrice,
    });
  }, [watchFulfillmentPrice, allVariants, watchItems]);

  const createOrder = async (e: any) => {
    e.preventDefault();
    if (watchItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one item to the order',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await createOrderMutation.executeAsync(form.getValues());
      if (response.success && response.data) {
        toast({
          title: 'Success',
          description: 'Order created successfully',
        });
        const createdId = String(response.data.id);
        form.setValue('items', []);
        if (onSubmitOrder) onSubmitOrder(true, createdId);
        if (onCloseDialog) onCloseDialog();
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to create order. Please try again.',
        variant: 'destructive',
      });
      if (onSubmitOrder) onSubmitOrder(false, null);
    }
  };
  return (
    <div>
      <Dialog
        open={isOpenOrderDialog}
        onOpenChange={(open) => {
          if (!open) {
            if (onCloseDialog) {
              onCloseDialog();
            }
          }
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Add Items for New Order</DialogTitle>
            <DialogDescription>
              <div className="flex items-center mt-3 mb-7">
                <div className="flex-1">
                  <h5 className="flex items-center text-sm font-semibold text-gray-900 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center mr-4">
                      {currentCustomer?.profileImageUrl && (
                        <img
                          className="w-10 h-10 rounded-full "
                          src={currentCustomer?.profileImageUrl}
                        ></img>
                      )}

                      {!currentCustomer?.profileImageUrl && (
                        <span className="text-xl font-semibold text-white">
                          {currentCustomer?.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    {currentCustomer?.name}
                  </h5>
                  <p className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Customer since{' '}
                    {typeof currentCustomer?.createdAt === 'string'
                      ? new Date(currentCustomer?.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })
                      : 'Unknown'}
                  </p>
                </div>
                <div className="flex items-start space-x-3 mr-5">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">
                      {currentCustomer?.phone || 'Not provided'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Shipping Address</p>
                    <p className="text-sm text-gray-600 whitespace-pre-line">
                      {currentCustomer?.address || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4"></div>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Form {...form}>
              <FormField
                control={form.control}
                name="items"
                render={({ field }) => (
                  <FormItem>
                    <OrderItemsEditor
                      value={field.value || []}
                      onChange={(items) => {
                        field.onChange(items);
                        form.trigger('items');
                      }}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Form>
          </div>

          {watchItems.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(totals.itemsSubtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Delivery Fee</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(totals.deliveryFee)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-semibold">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Left side: Cancel button */}
            <Button variant="outline" onClick={() => onCloseDialog?.()} className="sm:order-1">
              Cancel
            </Button>

            {/* Right side: Create Order button */}
            <Button
              className="gap-2 sm:order-2 sm:ml-auto"
              onClick={createOrder}
              disabled={watchItems.length === 0}
            >
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
